import { useEffect, useState, useRef } from 'react';
import Plot from 'react-plotly.js';
import { setXAxis, setYAxis } from '../../store/resultsSlice';
import { useSelector, useDispatch } from 'react-redux'
import { Card, Grid, Placeholder, Radio, RadioGroupField } from '@aws-amplify/ui-react';

const ResultsChart = ({ results, selected, isLoading }) => {
    const [xs, setXs] = useState([]);
    const [ys, setYs] = useState([]);
    const [yLabel, setYLabel] = useState("Max Weight")
    const chartContainerRef = useRef(null);
    const dispatch = useDispatch()
    const [chartDimensions, setChartDimensions] = useState({ width: '100%', height: '100%' });
    const xAxis = useSelector((state) => {
        return state?.results?.xAxis
    })
    const yAxis = useSelector((state) => {
        return state?.results?.yAxis
    })
    useEffect(() => {
        if (results[selected?.id]?.items) {
            const grouped = Object.groupBy(results[selected?.id]?.items, (item) => {
                const date = new Date(item.timestamp);
                return date.toDateString();
            });

            const x = [];
            const y = [];

            const sortedEntries = Object.entries(grouped).sort((a, b) =>
                new Date(a[0]) - new Date(b[0])
            );

            for (let [key, value] of sortedEntries) {
                if (value.size === 0) continue
                // IMPORTANT: Push the Date object, NOT the formatted string
                const date = new Date(key);
                switch (xAxis) {
                    case 'daysago':
                        x.push(Math.floor((date - Date.now()) / 86400000))
                        break;
                    default:
                        x.push(date); // Push actual Date object
                }

                switch (yAxis) {
                    case 'avg':
                        const sum = value?.reduce((acc, item) => acc + item.result, 0);
                        // Then calculate average
                        const avg = sum / value.length;
                        setYLabel("Average")
                        y.push(avg);
                        break;
                    case 'total':
                        const total = value?.reduce((acc, item) =>
                            acc + (item.result * item.repetitions), 0
                        );
                        setYLabel("Weight x Repetitions")
                        y.push(total);
                        break;
                    default:
                        setYLabel("Max Weight")
                        const maxObject = value?.reduce((max, item) =>
                            item.result > max.result ? item : max
                        );
                        y.push(maxObject.result);
                        break;
                }

            }

            setXs(x);
            setYs(y);
        }
    }, [results, selected, xAxis, yAxis]);

    // Handle resize with ResizeObserver
    useEffect(() => {
        if (!chartContainerRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const { width, height } = entry.contentRect;
                setChartDimensions({
                    width: width,
                    height: height
                });
            }
        });

        resizeObserver.observe(chartContainerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <div >
            {!isLoading &&
                <div
                    ref={chartContainerRef}
                    className='row'
                    id="tester"
                    style={{
                        width: '100%',
                        height: '500px',
                        position: 'relative'
                    }}
                >
                    <Plot
                        data={[
                            {
                                x: xs,
                                y: ys,
                                hovertemplate: xAxis === 'date'
                                    ? "<b>Date:</b> %{x|%b %d}<br>" +
                                    "<b>Value:</b> %{y:.1f} kg<br>" +
                                    "<extra></extra>"  // Removes trace name from hover
                                    : "<b>Days ago:</b> %{x}<br>" +
                                    "<b>Value:</b> %{y:.1f} <br>" +
                                    "<extra></extra>",
                                type: 'scatter',
                                mode: 'lines+markers',
                                marker: { color: 'red' },
                                name: "Results",
                            },
                        ]}
                        layout={{
                            autosize: true,
                            width: chartDimensions.width,
                            height: chartDimensions.height,
                            showlegend: false,
                            yaxis: {
                                autorange: true,
                                side: 'left',
                                tickformat: ".1f",
                                title: {
                                    text: yLabel,
                                    font: {
                                        size: 12
                                    }
                                }
                            },
                            xaxis: {
                                type: xAxis === 'date' ? 'date' : '-', // '-' for linear/numeric scale
                                tickformat: xAxis === 'date' ? '%b %d' : '.0f', // .0f for whole numbers
                                tickangle: -45,
                                hoverformat: xAxis === 'date' ? '%b %d' : '.0f',
                                title: {
                                    text: xAxis === 'date' ? 'Date' : 'Days Ago',
                                    font: { size: 12 }
                                }
                            },
                            title: 'Progress',
                            margin: {
                                l: 60, // Increased left margin for y-axis title
                                r: 30,
                                t: 50,
                                b: 70
                            }
                        }}
                        config={{
                            responsive: true,
                            displayModeBar: true
                        }}
                        style={{ width: '100%', height: '100%' }}
                        useResizeHandler={true}
                    />

                </div>}
            <Placeholder
                isLoaded={!isLoading}
                style={{ height: '500px' }}
            />
            <Grid
                columnGap="0.5rem"
                rowGap="0.5rem"
                templateColumns="1fr 1fr"
                templateRows="1fr"
            >
                <Card className='m-1'>
                    <RadioGroupField
                        legend="X axis"
                        name="x-axis"
                        variation="outlined"
                        size="small"
                        value={xAxis}
                        onChange={(e) => {
                            dispatch(setXAxis(e.target.value))
                        }}
                    >
                        <Radio value="date">Calendar date</Radio>
                        <Radio value="daysago">Days ago</Radio>

                    </RadioGroupField>
                </Card>
                <Card className=' m-1'>
                    <RadioGroupField
                        legend="Y axis"
                        name="y-axis"
                        variation="outlined"
                        size="small"
                        value={yAxis}
                        onChange={(e) => {
                            dispatch(setYAxis(e.target.value))
                        }}
                    >
                        <Radio value="avg">Average</Radio>
                        <Radio value="max">Maximum</Radio>
                        <Radio value="total">Total</Radio>
                    </RadioGroupField>
                </Card>
            </Grid>

        </div>
    );
};

export default ResultsChart;